import React from 'react'

import { Box, Stack, Typography, useTheme, alpha, keyframes } from '@mui/material'

import VLogoSVG from 'assets/images/vlogo.svg'
import LogoSVG from 'assets/images/logo.svg'

/**
 * Candidate replacements for the platform page loader (`components/feedback/Loader`).
 *
 * WHAT IS WRONG WITH THE CURRENT ONE, since that shapes every option here:
 *   1. It is a 157KB animated GIF — heavier than most of the page it covers, and it
 *      cannot inherit the theme, so it is the one element that ignores dark mode.
 *   2. It is sized in `vw` (8vw default), so on a wide monitor it renders enormous and
 *      on a laptop it is small. A loading indicator should be a fixed size; it is not
 *      content.
 *   3. It carries a dead 2000ms/2800ms `gif → v → logo` phase machine whose v and logo
 *      branches are commented out, so the timers run and the state changes and nothing
 *      ever renders differently. Same class of thing as the 3.8s LogoLoader floor that
 *      was removed from the scan path.
 *
 * Every option below is CSS-only, theme-aware, fixed-size, and has no phase machine.
 * They are deliberately shown at one size, side by side, on both surfaces a loader
 * appears on, because the only honest way to choose is to see them together.
 *
 * NOTE these are PROTOTYPES for a decision, not a shipped component. Whichever wins
 * gets extracted into `@repo/ui` once; do not import this file from product code.
 */

// ── shared motion ────────────────────────────────────────────────────────────────
const spin = keyframes`to { transform: rotate(360deg); }`
const dash = keyframes`
  0%   { stroke-dashoffset: 128; }
  50%  { stroke-dashoffset: 34; }
  100% { stroke-dashoffset: 128; }
`
/** Particle drawn INWARD to the mark, then released. The scale-down as it arrives is
 *  what sells "pulled" rather than "orbiting" — without it the dots read as a ring. */
const pullIn = keyframes`
  0%   { transform: rotate(var(--a)) translateX(26px) scale(1);   opacity: 0; }
  15%  { opacity: 1; }
  70%  { opacity: 1; }
  100% { transform: rotate(var(--a)) translateX(4px)  scale(0.3); opacity: 0; }
`
const orbit = keyframes`
  0%   { transform: rotate(0deg)   translateX(24px) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(24px) rotate(-360deg); }
`
const ripple = keyframes`
  0%   { transform: scale(0.55); opacity: 0.55; }
  100% { transform: scale(1.5);  opacity: 0; }
`
const breathe = keyframes`
  0%, 100% { transform: scale(1);    opacity: 1; }
  50%      { transform: scale(0.88); opacity: 0.7; }
`

// ── sidebar-logo motion ──────────────────────────────────────────────────────────
/** Purple glow behind the mark, breathing. */
const glowPulse = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(0.9); }
  50%      { opacity: 0.55; transform: scale(1.15); }
`
/**
 * SHEEN — a specular highlight passing over the mark, like light catching metal.
 *
 * Differs from a plain sweep in three ways that are what make it read as a surface
 * rather than a wash: the band is NARROW with a bright core (a wide soft gradient
 * reads as the logo changing colour, not as light moving over it); it is ANGLED, so
 * it crosses the letterforms diagonally the way a real highlight would; and it RESTS
 * between passes — the travel occupies only the first ~55% of the cycle, so you get a
 * glint rather than a conveyor belt.
 */
const sheen = keyframes`
  0%   { transform: translateX(-100%) skewX(-18deg); }
  55%  { transform: translateX(100%)  skewX(-18deg); }
  100% { transform: translateX(100%)  skewX(-18deg); }
`
/** The mark's own opacity breathing — no added chrome at all. */
const markPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
`
/** A hairline under the wordmark filling and emptying. */
const underline = keyframes`
  0%   { transform: scaleX(0);   transform-origin: left; }
  50%  { transform: scaleX(1);   transform-origin: left; }
  51%  { transform: scaleX(1);   transform-origin: right; }
  100% { transform: scaleX(0);   transform-origin: right; }
`

/**
 * `dark` is the PREVIEW SURFACE, deliberately not `theme.palette.mode`.
 *
 * The first version of this lab read the theme, which is the APP's mode — so the
 * dark-surface column rendered dark-on-dark and showed nothing. A side-by-side that
 * silently previews the same mode twice is worse than no comparison: it looks like
 * evidence. In the shipped component this will read the real theme; here it has to
 * follow the swatch it is drawn on.
 */
const Mark = ({ size = 19, dark }: { size?: number; dark?: boolean }) => (
  <Box
    component="img"
    src={VLogoSVG}
    alt=""
    aria-hidden
    sx={{
      width: size,
      height: 'auto',
      display: 'block',
      // The asset is a fixed dark mark; invert it to stay legible on a dark surface.
      ...(dark && { filter: 'brightness(0) invert(1)' }),
    }}
  />
)

/** Foreground for a given preview surface — same values the theme would resolve. */
const fgFor = (dark?: boolean) => (dark ? '#fafafa' : '#272727')

const Stage = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ position: 'relative', width: 64, height: 64, display: 'grid', placeItems: 'center' }}>
    {children}
  </Box>
)

// ── A. Arc on a track ────────────────────────────────────────────────────────────
const ArcLoader = ({ dark }: { dark?: boolean }) => {
  const fg = fgFor(dark)
  return (
    <Stage>
      <Box
        component="svg"
        viewBox="0 0 48 48"
        sx={{ position: 'absolute', width: 48, height: 48, animation: `${spin} 1.1s linear infinite` }}
      >
        <circle cx="24" cy="24" r="21" fill="none" strokeWidth={2}
          stroke={fg} strokeOpacity={0.12} />
        <circle cx="24" cy="24" r="21" fill="none" strokeWidth={2} strokeLinecap="round"
          stroke={fg} strokeOpacity={0.9} strokeDasharray="36 96" />
      </Box>
      <Mark dark={dark} />
    </Stage>
  )
}

// ── B. Growing/shrinking dash ────────────────────────────────────────────────────
const DashLoader = ({ dark }: { dark?: boolean }) => {
  const fg = fgFor(dark)
  return (
    <Stage>
      <Box
        component="svg"
        viewBox="0 0 48 48"
        sx={{ position: 'absolute', width: 48, height: 48, animation: `${spin} 1.4s linear infinite` }}
      >
        <circle cx="24" cy="24" r="21" fill="none" strokeWidth={2} strokeLinecap="round"
          stroke={fg} strokeOpacity={0.9}
          strokeDasharray="132"
          style={{ animation: `${dash} 1.4s ease-in-out infinite` }} />
      </Box>
      <Mark dark={dark} />
    </Stage>
  )
}

// ── C. Particles pulled in ───────────────────────────────────────────────────────
const PARTICLE_COUNT = 8
const ParticlePullLoader = ({ dark }: { dark?: boolean }) => {
  const fg = fgFor(dark)
  return (
    <Stage>
      {Array.from({ length: PARTICLE_COUNT }).map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: 4,
            height: 4,
            borderRadius: '50%',
            backgroundColor: fg,
            // Each particle owns its angle; one keyframe set serves all eight.
            ['--a' as string]: `${(360 / PARTICLE_COUNT) * i}deg`,
            animation: `${pullIn} 1.4s cubic-bezier(0.5, 0, 0.75, 0) infinite`,
            // Staggered so they arrive in sequence rather than as a collapsing ring —
            // a synchronised set reads as one shrinking circle, not as particles.
            animationDelay: `${(1.4 / PARTICLE_COUNT) * i}s`,
          }}
        />
      ))}
      <Mark dark={dark} />
    </Stage>
  )
}

// ── D. Single orbiting dot with a trail ──────────────────────────────────────────
const OrbitLoader = ({ dark }: { dark?: boolean }) => {
  const fg = fgFor(dark)
  return (
    <Stage>
      {[0, 1, 2].map(i => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: 5 - i,
            height: 5 - i,
            borderRadius: '50%',
            backgroundColor: alpha(fg, 0.9 - i * 0.28),
            animation: `${orbit} 1.3s linear infinite`,
            animationDelay: `${-i * 0.075}s`,
          }}
        />
      ))}
      <Mark dark={dark} />
    </Stage>
  )
}

// ── E. Radar ripple ──────────────────────────────────────────────────────────────
const RippleLoader = ({ dark }: { dark?: boolean }) => {
  const fg = fgFor(dark)
  return (
    <Stage>
      {[0, 1].map(i => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: `1.5px solid ${fg}`,
            animation: `${ripple} 1.8s ease-out infinite`,
            animationDelay: `${i * 0.9}s`,
          }}
        />
      ))}
      <Box sx={{ animation: `${breathe} 1.8s ease-in-out infinite` }}>
        <Mark dark={dark} />
      </Box>
    </Stage>
  )
}

// ═════════════════════════════════════════════════════════════════════════════════
// SIDEBAR LOGO ACTIVITY — the alternative to a page loader
// ═════════════════════════════════════════════════════════════════════════════════
// The page loader almost never appears, because nearly every surface uses skeletons
// (§19) and resolves before a spinner would be justified. So the honest question is
// whether the app needs a page loader at all, or just a quiet "working" signal in one
// fixed place. The sidebar mark is the only element present on every screen, never
// scrolls, and is already the thing your eye returns to — so it is the natural home.
//
// These are shown at BOTH sidebar states, since the sidebar collapses: the 120x25
// wordmark and the 25px V.

const SidebarFrame = ({ dark, children }: { dark?: boolean; children: React.ReactNode }) => (
  <Box
    sx={{
      width: 190,
      py: 2.5,
      px: '14px',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: dark ? '#141417' : '#ffffff',
      borderRight: `1px solid ${dark ? '#2a2a2e' : '#eaeaea'}`,
    }}
  >
    {children}
  </Box>
)

const LogoImg = ({ dark, collapsed }: { dark?: boolean; collapsed?: boolean }) => (
  <Box
    component="img"
    src={collapsed ? VLogoSVG : LogoSVG}
    alt=""
    aria-hidden
    sx={{
      height: 25,
      width: collapsed ? 'auto' : 120,
      display: 'block',
      position: 'relative',
      zIndex: 1,
      ...(dark && { filter: 'brightness(0) invert(1)' }),
    }}
  />
)

/** F · Purple glow behind the mark. */
const GlowLogo = ({ dark, collapsed }: { dark?: boolean; collapsed?: boolean }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        // Sized off the mark so it hugs the V when collapsed and the wordmark when not.
        inset: collapsed ? -10 : '-10px -14px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha('#7F48DF', 0.9)} 0%, ${alpha('#7F48DF', 0)} 70%)`,
        filter: 'blur(6px)',
        animation: `${glowPulse} 1.6s ease-in-out infinite`,
      }}
    />
    <LogoImg dark={dark} collapsed={collapsed} />
  </Box>
)

/** G · Light sweeping across the mark. */
const SweepLogo = ({ dark, collapsed }: { dark?: boolean; collapsed?: boolean }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', overflow: 'hidden' }}>
    <LogoImg dark={dark} collapsed={collapsed} />
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        // EXACTLY the logo's box. `mask-size: contain` scales the glyph to the ELEMENT,
        // so a band wider than the logo draws the mask at the wrong size and anchors it
        // off to the left — it never overlaps the letters, however bright the gradient.
        // Same box + translateX(-100% → 100%) is how MUI's own skeleton wave does it.
        inset: 0,
        zIndex: 2,
        // FOIL, not a coloured band. This matches the tag preview's logo gloss
        // (TagCanvasEditor: linear-gradient(135deg, rgba(255,255,255,.5) → .1 → 0),
        // screen-blended) and the brand mark's own sheen stops (#FFFFFF → #DED8EC).
        // The earlier saturated-purple version read as a highlighter passing over the
        // logo rather than light moving across a metallic surface.
        //
        // The tint has to flip with the surface. On light the mark is dark, so a white
        // sheen LIFTS it. On dark the mark is inverted to white, and white-on-white is
        // nothing — there a cool violet-grey reads as the foil's reflection instead,
        // the same pairing the brand asset uses on its dark tile.
        background: dark
          ? `linear-gradient(135deg,
              rgba(160,148,196,0) 30%,
              rgba(160,148,196,0.55) 46%,
              rgba(120,106,160,0.8) 52%,
              rgba(160,148,196,0) 70%)`
          : `linear-gradient(135deg,
              rgba(255,255,255,0) 30%,
              rgba(255,255,255,0.55) 46%,
              rgba(222,216,236,0.95) 52%,
              rgba(255,255,255,0) 70%)`,
        mixBlendMode: dark ? 'normal' : 'screen',
        animation: `${sheen} 2.6s cubic-bezier(0.4, 0, 0.25, 1) infinite`,
        // url() MUST be quoted: Vite inlines these SVGs as data: URIs whose payload
        // contains raw quotes and parens, so an unquoted url() is invalid CSS and the
        // browser drops the whole declaration — leaving the band unmasked, i.e. a
        // rectangle of light sitting on top of the logo instead of inside it.
        WebkitMaskImage: `url("${collapsed ? VLogoSVG : LogoSVG}")`,
        maskImage: `url("${collapsed ? VLogoSVG : LogoSVG}")`,
        WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'left center', maskPosition: 'left center',
        WebkitMaskSize: 'contain', maskSize: 'contain',
      }}
    />
  </Box>
)

/** H · The mark itself breathing — no added chrome. */
const PulseLogo = ({ dark, collapsed }: { dark?: boolean; collapsed?: boolean }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', animation: `${markPulse} 1.4s ease-in-out infinite` }}>
    <LogoImg dark={dark} collapsed={collapsed} />
  </Box>
)

/** I · Hairline under the mark, filling and emptying. */
const UnderlineLogo = ({ dark, collapsed }: { dark?: boolean; collapsed?: boolean }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch' }}>
    <LogoImg dark={dark} collapsed={collapsed} />
    <Box
      aria-hidden
      sx={{
        height: 2,
        mt: '5px',
        borderRadius: 1,
        backgroundColor: '#7F48DF',
        animation: `${underline} 1.5s ease-in-out infinite`,
      }}
    />
  </Box>
)

interface SidebarOption {
  key: string
  name: string
  render: (dark: boolean, collapsed: boolean) => React.ReactNode
  note: string
}

const SIDEBAR_OPTIONS: SidebarOption[] = [
  {
    key: 'glow',
    name: 'F · Purple glow behind the mark',
    render: (dark, collapsed) => <GlowLogo dark={dark} collapsed={collapsed} />,
    note: 'Closest to the brief. Reads as the mark being "lit" rather than as a control, so it never looks like something to click. Risk: a blurred radial on a dark sidebar can look like a rendering artefact rather than intent.',
  },
  {
    key: 'sweep',
    name: 'G · Sheen across the mark',
    render: (dark, collapsed) => <SweepLogo dark={dark} collapsed={collapsed} />,
    note: 'The same foil gloss as the tag preview\'s logo sheen and the brand mark\'s own gradient stops — white lifting to #DED8EC on light, a cool violet-grey on dark where the mark is inverted and white-on-white would show nothing. Travels diagonally and rests between passes — narrow bright core, angled, and resting between passes so it reads as a glint rather than a conveyor belt. Masked to the glyph, so the light crosses the letterforms rather than a rectangle sitting on them. Shares its logic with the skeleton shimmer already on screen, so "loading" looks like one thing everywhere.',
  },
  {
    key: 'pulse',
    name: 'H · The mark breathes (no chrome)',
    render: (dark, collapsed) => <PulseLogo dark={dark} collapsed={collapsed} />,
    note: 'Nothing is added — the logo just fades in and out. Quietest, and the only one with zero risk of reading as an artefact. Also the easiest to miss, and opacity alone can look like a rendering glitch on a slow frame.',
  },
  {
    key: 'underline',
    name: 'I · Hairline fills under the mark',
    render: (dark, collapsed) => <UnderlineLogo dark={dark} collapsed={collapsed} />,
    note: 'The only option that reads as PROGRESS rather than activity, because it has a direction. That is a liability, not a feature: it implies a measurable amount of work remaining, which we do not know. Included to make that trade-off visible.',
  },
]

interface Option {
  key: string
  name: string
  render: (dark: boolean) => React.ReactNode
  note: string
}

const OPTIONS: Option[] = [
  {
    key: 'arc',
    name: 'A · Arc on a track',
    render: (dark: boolean) => <ArcLoader dark={dark} />,
    note: 'What the certificate scan loader already uses. Choosing it makes the two surfaces match, which is worth more than novelty — a customer scanning a tag and a brand loading their dashboard would see one company.',
  },
  {
    key: 'dash',
    name: 'B · Growing dash',
    render: (dark: boolean) => <DashLoader dark={dark} />,
    note: 'The classic Material indeterminate. Rejected on the scan loader: the arc shrinks to ~3% of the circle, and at that size it reads as a stray tick orbiting the mark rather than a spinner. Included so the comparison is on the table rather than asserted.',
  },
  {
    key: 'particles',
    name: 'C · Particles pulled in',
    render: (dark: boolean) => <ParticlePullLoader dark={dark} />,
    note: 'Dots drawn inward and consumed by the mark. Staggered so they arrive in sequence — synchronised, they read as one collapsing ring. Says "gathering" rather than "waiting", which fits verification. Busiest of the five; the risk is that it reads as decoration on a surface shown dozens of times a day.',
  },
  {
    key: 'orbit',
    name: 'D · Orbit with trail',
    render: (dark: boolean) => <OrbitLoader dark={dark} />,
    note: 'Three dots at decreasing size and opacity, offset in time to fake motion blur. Quieter than particles, more character than an arc. No SVG at all — three divs.',
  },
  {
    key: 'ripple',
    name: 'E · Radar ripple',
    render: (dark: boolean) => <RippleLoader dark={dark} />,
    note: 'Rings expanding outward from a breathing mark. The only option with no rotation, so it does not compete with a spinner elsewhere on screen. Slowest read — the 1.8s cycle can feel stalled on a fast load.',
  },
]

export const LoadersLab = () => {
  const theme = useTheme()

  return (
    <Stack spacing={6} sx={{ pb: 8 }}>
      <Box>
        <Typography variant="body1" sx={{ fontSize: 20, pb: 1 }}>
          Page loader candidates
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6, maxWidth: 720, display: 'block' }}>
          Replacements for the current platform loader, which is a 157KB GIF sized in{' '}
          <code>vw</code> (so it is huge on a wide monitor), cannot follow dark mode, and
          runs a 2000/2800ms phase machine whose later phases are commented out — the
          timers fire and nothing changes. Every option here is CSS-only, theme-aware and
          a fixed size.
        </Typography>
      </Box>

      {/* Shown on BOTH surfaces at once: a loader that reads well on white can vanish on
          the dark card it will actually sit on, and picking from one background is how
          that gets missed. */}
      {OPTIONS.map(opt => (
        <Stack key={opt.key} spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 15, fontWeight: 600 }}>
            {opt.name}
          </Typography>
          <Stack direction="row" spacing={0}>
            {(['light', 'dark'] as const).map(surface => (
              <Box
                key={surface}
                sx={{
                  flex: 1,
                  display: 'grid',
                  placeItems: 'center',
                  py: 6,
                  backgroundColor: surface === 'light' ? '#ffffff' : '#141417',
                  border: `1px solid ${theme.palette.primary[100]}`,
                  ...(surface === 'light'
                    ? { borderRadius: '8px 0 0 8px', borderRight: 'none' }
                    : { borderRadius: '0 8px 8px 0' }),
                  // Force the child subtree to resolve the opposite mode's colours.
                  colorScheme: surface,
                  color: surface === 'light' ? '#272727' : '#fafafa',
                }}
              >
                {opt.render(surface === 'dark')}
              </Box>
            ))}
          </Stack>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.6, maxWidth: 720, display: 'block' }}>
            {opt.note}
          </Typography>
        </Stack>
      ))}

      <Box sx={{ pt: 4 }}>
        <Typography variant="body1" sx={{ fontSize: 20, pb: 1 }}>
          Sidebar logo activity
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6, maxWidth: 720, display: 'block' }}>
          An alternative to a page loader rather than an addition to one. Nearly every
          surface already uses skeletons, so the full-page loader almost never appears —
          which raises the question of whether the app needs one at all, or just a quiet
          "working" signal in one fixed place. The sidebar mark is on every screen, never
          scrolls, and is already where the eye returns. Shown at both sidebar states,
          since it collapses.
        </Typography>
      </Box>

      {SIDEBAR_OPTIONS.map(opt => (
        <Stack key={opt.key} spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 15, fontWeight: 600 }}>
            {opt.name}
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            {([false, true] as const).map(dark =>
              ([false, true] as const).map(collapsed => (
                <SidebarFrame key={`${dark}-${collapsed}`} dark={dark}>
                  {opt.render(dark, collapsed)}
                </SidebarFrame>
              )),
            )}
          </Stack>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.6, maxWidth: 720, display: 'block' }}>
            {opt.note}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

export default LoadersLab
